import { Injectable, inject } from '@angular/core';
import { Country, PhoneAddress, PhoneType } from '@wdx/shared/utils';
import { Observable, map } from 'rxjs';
import { IFormDynamicData } from '../../interfaces';

@Injectable()
export class TelephoneService {
    private dynamicDataService = inject(IFormDynamicData);
    private regexChar = '0';

    fieldValueToTelephonePattern$(
        fieldValue: {
            number: string;
            type: PhoneType;
            country: string;
        },
        asPatternMask = false,
    ): Observable<string> {
        return this.dynamicDataService.getCountries().pipe(
            map((countries) => {
                const number = fieldValue.number?.replace(/\s+/g, '');
                const country = countries.find(
                    (country) => country.isoCode === fieldValue.country,
                );
                const phoneType = fieldValue.type;
                if (asPatternMask) {
                    return this.getFullPattern(
                        number,
                        phoneType,
                        country as Country,
                    ).join('');
                }
                return this.formatPhoneNumber(
                    number,
                    phoneType,
                    country as Country,
                );
            }),
        );
    }

    getCountryPattern(type: PhoneType, country: Country): string {
        const isMobile = [
            PhoneType.MobilePersonal,
            PhoneType.MobileOther,
            PhoneType.MobileWork,
        ].includes(type);
        return (isMobile ? country.mobileRegex : country.phoneRegex) as string;
    }

    public formatPhoneNumber(
        phoneNumber: string,
        phoneType: PhoneType,
        country: Country,
    ) {
        if (!phoneNumber || !country || !phoneType) {
            return '';
        }

        const number = phoneNumber?.replace(/\s+/g, '');
        const fullPattern = this.getFullPattern(
            phoneNumber,
            phoneType,
            country,
        );
        const fullNumber = (country?.dialCode || '') + number;
        const indexMap = fullPattern.reduce((accumulator, char, i) => {
            if (char === this.regexChar) {
                accumulator = [...accumulator, i];
            }
            return accumulator;
        }, [] as number[]);
        [...fullNumber].forEach((num, i) =>
            fullPattern.splice(indexMap[i], 1, num),
        );
        const lastUsedIndex = indexMap[fullNumber.length - 1];
        return fullPattern.slice(0, lastUsedIndex + 1).join('');
    }

    private getFullPattern(
        phoneNumber: string,
        phoneType: PhoneType,
        country: Country,
    ) {
        if (!phoneNumber || !country || !phoneType) {
            return [''];
        }
        const number = phoneNumber?.replace(/\s+/g, '');
        const dialCodePattern = `+${Array.from(
            { length: country?.dialCode?.length || 0 },
            () => this.regexChar,
        ).join('')}`;
        const numberPattern =
            this.getCountryPattern(phoneType, country as Country) ||
            Array.from({ length: number.length }, () => this.regexChar).join(
                '',
            );
        return [...dialCodePattern, ' ', ...numberPattern];
    }
}
